import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Select from 'react-select';
import {connect} from 'react-redux';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from 'react-query';
import {X} from 'lucide-react';
import * as Actions from '../../../store/actions';
import {Constants} from '../../../helpers';
import {buildQueryParams} from '../../../helpers/queryParamsBuilder';
import {useDefaultCountry} from '../../../hooks/useDefaultCountry';
import {useAllApps} from '../../../hooks/useApps';
import {customer360LookupDropdownOptions} from '../../Customer360/LayoutConstants';
import {
  customStyles,
  DropdownIndicator,
  CountryInput,
  PrimaryButton,
  Modal,
  CreateAppForm,
} from '../../../components';
import {plus, times} from '../../../assets/images/images';
import EasyLookupDetails from '../EasyLookupDetails/index';
import GlobalSearchResultsModal from '../GlobalSearchResultsModal';
import Nigeria from '../../../components/CountryLookups/Nigeria';
import Ghana from '../../../components/CountryLookups/Ghana';
import Uganda from '../../../components/CountryLookups/Uganda';
import Kenya from '../../../components/CountryLookups/Kenya';
import SouthAfrica from '../../../components/CountryLookups/SouthAfrica';
import Zimbabwe from '../../../components/CountryLookups/Zimbabwe';
import Zambia from '../../../components/CountryLookups/Zambia';

const businessIds = ['cac', 'cac/basic', 'cac_advance', 'global_search'];

const CountryComponentMap = ({lookupInputs, ...props}) => {
  const country = lookupInputs?.country?.value;
  const countryComponentMap = {
    ghana: Ghana,
    uganda: Uganda,
    kenya: Kenya,
    zimbabwe: Zimbabwe,
    zambia: Zambia,
    'south-africa': SouthAfrica,
  };
  const Country = countryComponentMap[country] || Nigeria;
  return <Country lookupInputs={lookupInputs} {...props} />;
};

function SingleIndividualLookupModal({
  performLookup,
  changeLookupPage,
  lookup: {lookupPage, loading, l_loading},
  open,
  setOpen,
  ...otherProps
}) {
  const {customer360, customer_id, customer360Dropdown} = otherProps;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {userDefaultCountry, isLoading: defaultLoading} = useDefaultCountry();
  const {userApps, isLoading} = useAllApps();

  const activeCountryName = sessionStorage.getItem('active-country');
  const activeCountry = useMemo(
    () =>
      Constants.lookups.countries.find(
        country => country?.value === activeCountryName,
      ),
    [activeCountryName],
  );

  const [lookupInputs, setLookupInputs] = useState({
    ...Constants?.lookups?.initialLookUpState,
    app: (!isLoading && userApps && userApps[0]) || {},
    country: activeCountry ? activeCountry : userDefaultCountry,
  });
  const [step, setStep] = useState(lookupPage !== undefined ? lookupPage : 1);
  const [enable, setEnable] = useState(true);
  const [newAppForm, setNewAppForm] = useState(false);

  useEffect(() => {
    setLookupInputs(prev => ({
      ...prev,
      country: activeCountry || userDefaultCountry,
      app: (!isLoading && userApps && userApps[0]) || {},
    }));
  }, [userDefaultCountry, userApps, isLoading, activeCountry]);

  useEffect(() => {
    setStep(lookupPage !== undefined ? lookupPage : 1);
  }, [lookupPage]);

  const {
    country,
    type,
    lookupValue = '',
    app,
    surname,
  } = useMemo(() => lookupInputs, [lookupInputs]);

  const getCountryIDs = useCallback(
    key =>
      Constants.lookups.getIDType(
        key || (!defaultLoading && userDefaultCountry?.value) || 'nigeria',
      ),
    [userDefaultCountry, defaultLoading],
  );

  const getFilteredOptions = useMemo(() => {
    const allOptions = getCountryIDs(country?.value);
    if (customer360Dropdown && country?.value === 'nigeria') {
      return customer360LookupDropdownOptions;
    }
    return allOptions?.filter(option => !businessIds.includes(option?.value));
  }, [country?.value, customer360Dropdown, getCountryIDs]);

  const handleLookup = useCallback(() => {
    if (!app.value) {
      return toast.error('Please select an app');
    }

    const url = Constants.lookups.checkLookupType(
      country?.value || 'nigeria',
      type?.value,
      lookupValue,
    );
    const queryParamString = buildQueryParams(lookupInputs);
    let lookupParams = `${url}${
      queryParamString ? `&${queryParamString}` : ''
    }`;

    if (lookupParams?.includes('country_code=&')) {
      lookupParams = lookupParams?.replace('country_code=&', '');
    }

    performLookup({
      data: lookupParams.replace(/\s/g, ''),
      type: type?.value,
      appId: app?.value.replace(/\s/g, ''),
      ...(type.value === 'nuban' && {account_number: lookupValue}),
      customer360,
      navigate,
      queryClient,
      setOpen,
      customer_id,
    });
    changeLookupPage(1);

    if (!loading && type?.value === 'global_search') {
      return setLookupInputs({...Constants.lookups.initialLookUpState, type});
    }

    !loading &&
      setLookupInputs({
        ...Constants.lookups.initialLookUpState,
        type: {},
        app: {},
        country: userDefaultCountry,
      });
  }, [
    app?.value,
    changeLookupPage,
    country?.value,
    customer360,
    customer_id,
    loading,
    lookupInputs,
    lookupValue,
    navigate,
    performLookup,
    queryClient,
    setOpen,
    type,
    userDefaultCountry,
  ]);

  const ngPass =
    lookupInputs?.type?.value === 'passport' &&
    lookupInputs?.country?.value === 'nigeria';

  useEffect(() => {
    if (ngPass && !!surname && lookupValue) {
      setEnable(false);
    } else if (!ngPass && lookupValue) {
      setEnable(false);
    } else {
      setEnable(true);
    }
  }, [lookupValue, ngPass, surname]);

  const getModalClasses = () => {
    const baseClasses = 'overflow-y-auto absolute bottom-0 sm:relative';
    if (step === 1) {
      return `max-w-[528px] h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg ${baseClasses} lookup-modal`;
    }
    return `max-w-[516px] rounded-t-none sm:rounded-lg h-fit max-h-full lookup-modal ${baseClasses}`;
  };

  const renderModalContent = () => {
    if (step === 2) {
      return lookupInputs?.type?.value === 'global_search' ? (
        <GlobalSearchResultsModal open={open} setOpen={setOpen} />
      ) : (
        <EasyLookupDetails open={open} setOpen={setOpen} />
      );
    }

    return (
      <div>
        <header className="flex items-center bg-[#F9F9F9] justify-between py-3 px-6 rounded-t-xl border-[0.5px] border-transparent border-b-[#EFEFEF]">
          <h3 className="font-semibold text-deepBlue text-[18px]">
            Single Lookup
          </h3>
          <button
            onClick={() => setOpen(false)}
            className="bg-[#EDEDED] rounded-md p-1.5"
          >
            <X size={16} color="#242424" />
          </button>
        </header>
        <form className="text-left" style={{opacity: l_loading && 0.4}}>
          <div className="py-4 px-6">
            <CountryInput
              value={
                activeCountry ||
                userDefaultCountry ||
                lookupInputs?.country ||
                Constants?.lookups?.countries[0]
              }
              onChange={country => {
                setLookupInputs({
                  ...lookupInputs,
                  ...Constants?.lookups?.initialLookUpState,
                  country: country || userDefaultCountry,
                });
                const activeName = country?.value || userDefaultCountry?.value;
                sessionStorage.setItem('active-country', activeName);
              }}
              label="Select country"
            />
            <label htmlFor="app" className="relative mb-1 mt-6">
              Select app
            </label>
            <Select
              defaultValue={lookupInputs?.app || (!isLoading && userApps[0])}
              value={lookupInputs?.app || (!isLoading && userApps[0])}
              onChange={app => setLookupInputs({...lookupInputs, app})}
              options={userApps}
              placeholder="Select app"
              components={{DropdownIndicator}}
              classNamePrefix="react-select"
              styles={customStyles}
              isDisabled={isLoading}
            />
            <button
              className="flex items-center gap-[2px] ml-auto mt-2"
              onClick={() => setNewAppForm(prev => !prev)}
              type="button"
            >
              <img
                src={newAppForm ? times : plus}
                alt=""
                width={12}
                height={12}
              />
              <span className="text-xs font-medium text-body">
                {newAppForm ? 'Close' : 'Create a new app'}
              </span>
            </button>
            {newAppForm && (
              <div className="p-6 mt-2 rounded bg-white80 animate-dropdown">
                <h3 className="text-base font-medium text-grey">Create App</h3>
                <CreateAppForm setOpen={() => setNewAppForm(prev => !prev)} />
              </div>
            )}
            <label htmlFor="industry" className="relative mt-6 mb-1">
              Select ID Type
            </label>
            <Select
              defaultValue={lookupInputs?.type}
              value={lookupInputs?.type}
              onChange={type => setLookupInputs({...lookupInputs, type})}
              options={getFilteredOptions}
              placeholder="Select an ID type"
              components={{DropdownIndicator}}
              classNamePrefix="react-select"
              styles={customStyles}
              menuPlacement="auto"
              isSearchable={false}
              key={`individual-single-${country?.value}`}
            />
            <CountryComponentMap
              lookupInputs={lookupInputs}
              setLookupInputs={setLookupInputs}
            />
          </div>
          <div className="w-full px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
            <PrimaryButton
              disabled={enable}
              buttonText="Lookup"
              onClick={handleLookup}
              className="w-full !h-10"
              loading={loading}
            />
          </div>
        </form>
      </div>
    );
  };

  return (
    <Modal
      show={open}
      onClose={setOpen}
      modalPosition="justify-center sm:justify-end"
      extraModalClasses={getModalClasses()}
    >
      {renderModalContent()}
    </Modal>
  );
}

export default connect(state => state, Actions)(SingleIndividualLookupModal);
